/**
 * useEvaluateFormAccess
 *
 * Mirrors `firestore.rules` for forms so the UI can disable what a write would
 * reject. Consumers are `useFormStore`, `FormUI`, `ArTableUI` and
 * `useEntityRecordUI` — all of which pass the ids they read off `formStore`.
 *
 * Group access comes from one fetch of the form doc; the draft check reads the
 * live `formStore.state.formVersion.data.state` leaf instead of fetching the
 * version, so closing a form flips `canWrite` off the moment the store echoes
 * the new state. That is why no caller has to invalidate this hook by hand.
 *
 * The two are returned separately on purpose. `canWrite` folds the grant and the
 * version-state gate together and answers "may write right now", which is what
 * disables a control. `hasWriteAccess` is the grant alone, and is what a caller
 * must consult before telling the reader they lack permission — a frozen version
 * denies nobody, and saying so to a user holding `formsWrite` is false (SYD-2455).
 *
 * `versionId` therefore only gates non-emptiness — the draft check is bound to
 * the version the store currently has open, not to the id passed in. Every
 * caller today passes a store-derived id, so the two agree; a caller asking
 * about some other version (a version-list row, say) would get an answer about
 * the open one instead.
 *
 * Navigating A→B is briefly inconsistent for the same reason: the loading
 * update keeps A's `hasWriteAccess` while `isDraftVersion` is already B's live
 * leaf, so `canWrite` can flash true from A's grant plus B's draft state. This
 * only enables UI — `firestore.rules` is what actually gates the write.
 *
 * Imports `formStore` by module path, not through `@/store`: the store barrel
 * reaches `useFormStore`, which calls this hook.
 */

import { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { useSnapshot } from 'valtio';
import { useGet } from './useGet';
import { Form } from '#types/System.types';
import { UserContext } from '../../providers/UserContext';
import { formStore } from '../../store/form/formStore';

/** Result of form access evaluation. */
interface FormAccessResult {
  /** Whether the user can read the form */
  canRead: boolean;
  /** Whether the user can write to the form right now — the grant AND a draft version */
  canWrite: boolean;
  /**
   * Whether the user holds write access to the form at all: `userRights.formsWrite`
   * or membership of a group in `form.accessGroups.writeGroupIds`. The grant before
   * the version-state gate, so it stays true while a closed or approved version has
   * `canWrite` false. Use it to decide whether a denial may be reported, never to
   * decide whether a control is live.
   */
  hasWriteAccess: boolean;
  /** Whether the group-access check is in progress */
  loading: boolean;
  /** Any error that occurred during the group-access check */
  error: Error | null;
}

/** Group-level access from the form doc, before the draft-state gate. */
interface GroupAccess {
  canRead: boolean;
  hasWriteAccess: boolean;
  loading: boolean;
  error: Error | null;
}

/** Props for useEvaluateFormAccess hook */
interface UseEvaluateFormAccessProps {
  /** ID of the form to check access for */
  formId: string | undefined;
  /** ID of the form version to check access for */
  versionId: string | undefined;
  /** Whether to enable access checking */
  enabled?: boolean;
}

const NO_ACCESS: GroupAccess = {
  canRead: false,
  hasWriteAccess: false,
  loading: false,
  error: null,
};

/**
 * Hook to evaluate form and version access based on Firestore rules.
 *
 * IMPORTANT: This hook MUST mirror the current permission logic in `firestore.rules`.
 *
 * Current production access model (forms) is:
 * - Read: `userRights.formsRead` OR `userRights.formsWrite` OR a security group of the user is in `form.accessGroups.readGroupIds`
 * - Write: `userRights.formsWrite` OR a security group of the user is in `form.accessGroups.writeGroupIds`,
 *   AND the form version state is `draft`
 *
 * @returns Access evaluation result containing read/write permissions
 */
export const useEvaluateFormAccess = ({
  formId,
  versionId,
  enabled = true
}: UseEvaluateFormAccessProps): FormAccessResult => {
  const { get } = useGet();
  const { user, userRights, userSecurityGroups } = useContext(UserContext);
  const userId = user?.uid ?? null;
  const formSnap = useSnapshot(formStore.state);
  const isDraftVersion = formSnap.formVersion.data?.state === 'draft';
  const [groupAccess, setGroupAccess] = useState<GroupAccess>(NO_ACCESS);

  const checkGroupAccess = useCallback(async () => {
    if (!formId || !enabled || !userId) {
      setGroupAccess(NO_ACCESS);
      return;
    }

    setGroupAccess(prev => ({ ...prev, loading: true }));

    try {
      const formDoc = await get<Form>('forms', formId);

      if (!formDoc) {
        setGroupAccess(NO_ACCESS);
        return;
      }

      const readGroupIds = formDoc.accessGroups?.readGroupIds ?? [];
      const writeGroupIds = formDoc.accessGroups?.writeGroupIds ?? [];

      const hasReadGroupAccess = readGroupIds.some(groupId => userSecurityGroups.includes(groupId));
      const hasWriteGroupAccess = writeGroupIds.some(groupId => userSecurityGroups.includes(groupId));

      const hasGlobalFormsRead = userRights['formsRead'] === true;
      const hasGlobalFormsWrite = userRights['formsWrite'] === true;

      setGroupAccess({
        canRead: hasGlobalFormsRead || hasGlobalFormsWrite || hasReadGroupAccess,
        hasWriteAccess: hasGlobalFormsWrite || hasWriteGroupAccess,
        loading: false,
        error: null,
      });

    } catch (error) {
      setGroupAccess({ ...NO_ACCESS, error: error as Error });
    }
  }, [enabled, formId, get, userSecurityGroups, userRights, userId]);

  useEffect(() => {
    void Promise.resolve().then(() => checkGroupAccess());
  }, [checkGroupAccess]);

  return useMemo(() => ({
    canRead: groupAccess.canRead,
    canWrite: !!versionId && groupAccess.hasWriteAccess && isDraftVersion,
    hasWriteAccess: groupAccess.hasWriteAccess,
    loading: groupAccess.loading,
    error: groupAccess.error,
  }), [groupAccess, versionId, isDraftVersion]);
};
